/**
 * In-app first-access invite JWTs (one typ per approval surface).
 * The client does not verify the signature — only reads claims for UI / redirects.
 */
export type ClientApprovalService = 'proto' | 'labdip' | 'bulk' | 'sourcing' | 'trim' | 'print_embroidery';

export interface ClientFirstAccessInviteDecoded {
  email: string;
  name?: string | null;
  service: ClientApprovalService;
  entityId: number;
}

/** Settings → Send in-app invite (no specific service). */
export const CLIENT_IN_APP_SETTINGS_INVITE_TYP = 'client_in_app_dashboard_first_access';

/** Default landing after Settings in-app invite (first-access / login). */
export const CLIENT_IN_APP_SETTINGS_DEFAULT_REDIRECT = '/dashboard';

const ENTITY_BY_TYP: Record<string, ClientApprovalService> = {
  proto_in_app_first_access: 'proto',
  labdip_in_app_first_access: 'labdip',
  bulk_in_app_first_access: 'bulk',
  sourcing_in_app_first_access: 'sourcing',
  trim_in_app_first_access: 'trim',
  print_embroidery_in_app_first_access: 'print_embroidery',
};

/** Preferred JWT claim for the resource id per `typ` (backend may use only one). */
const ID_KEYS: Record<ClientApprovalService, readonly string[]> = {
  proto: ['proto_id'],
  labdip: ['labdip_id'],
  bulk: ['bulk_id'],
  sourcing: ['sourcing_id'],
  trim: ['trim_id'],
  print_embroidery: ['print_embroidery_id'],
};

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = String(token || '').split('.');
  if (parts.length !== 3) return null;
  try {
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4;
    if (pad) b64 += '='.repeat(4 - pad);
    const json = atob(b64);
    const payload = JSON.parse(json) as Record<string, unknown>;
    return payload && typeof payload === 'object' ? payload : null;
  } catch {
    return null;
  }
}

function readExpSeconds(payload: Record<string, unknown>): number | null {
  const exp = payload['exp'];
  if (typeof exp === 'number' && Number.isFinite(exp)) return exp;
  if (typeof exp === 'string' && exp.trim() !== '') {
    const n = Number(exp);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function readFiniteId(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (raw != null && String(raw).trim() !== '') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function readEntityId(payload: Record<string, unknown>, service: ClientApprovalService): number | null {
  const keys = ID_KEYS[service];
  for (const k of keys) {
    const v = readFiniteId(payload[k]);
    if (v != null) return v;
  }
  const generic = readFiniteId(payload['entity_id'] ?? payload['resource_id'] ?? payload['id']);
  return generic;
}

/**
 * Decode first-access invite JWT for any supported approval service.
 *
 * @param options.ignoreExpiration When true, expired JWTs still decode (re-open email link).
 */
export function decodeClientFirstAccessInviteJwt(
  token: string,
  options?: { ignoreExpiration?: boolean },
): ClientFirstAccessInviteDecoded | null {
  const payload = decodeJwtPayload(token);
  if (!payload) return null;
  const typ = String(payload['typ'] ?? payload['type'] ?? '');
  const service = ENTITY_BY_TYP[typ];
  if (!service) return null;

  const exp = readExpSeconds(payload);
  if (!options?.ignoreExpiration && exp != null && Date.now() / 1000 > exp) {
    return null;
  }

  const email = String(payload['email'] ?? '').trim();
  if (!email) return null;

  const entityId = readEntityId(payload, service);
  if (entityId == null) return null;

  const name = payload['name'] != null ? String(payload['name']) : null;

  return { email, name, service, entityId };
}

/** Query param name used on the authenticated list route for deep-link open dialog. */
export function inAppOpenQueryParamForService(service: ClientApprovalService): string {
  switch (service) {
    case 'proto':
      return 'openProto';
    case 'labdip':
      return 'openLabdip';
    case 'bulk':
      return 'openBulk';
    case 'sourcing':
      return 'openSourcing';
    case 'trim':
      return 'openTrim';
    case 'print_embroidery':
      return 'openPrintEmbroidery';
    default:
      return 'open';
  }
}

export function listPathForApprovalService(service: ClientApprovalService): string {
  switch (service) {
    case 'proto':
      return '/protos';
    case 'labdip':
      return '/labdips';
    case 'bulk':
      return '/bulks';
    case 'sourcing':
      return '/sourcing';
    case 'trim':
      return '/trims';
    case 'print_embroidery':
      return '/print-embroidery';
    default:
      return '/dashboard';
  }
}

/** URL path + query for post-login / post-first-access navigation (in-app approval). */
export function buildInAppApprovalDeepLink(target: ClientFirstAccessInviteDecoded): string {
  const base = listPathForApprovalService(target.service);
  const q = inAppOpenQueryParamForService(target.service);
  return `${base}?${q}=${encodeURIComponent(String(target.entityId))}`;
}

/** Normalise redirect paths from invite JWT / context / query. */
export function normalizeClientPostAuthRedirect(url: string | null | undefined): string | null {
  const raw = String(url ?? '').trim();
  if (!raw || !raw.startsWith('/')) return null;
  // Brief product experiment used `/products`; default is dashboard again.
  if (raw === '/products') return CLIENT_IN_APP_SETTINGS_DEFAULT_REDIRECT;
  return raw;
}

/**
 * Decode Settings in-app invite JWT (no proto/service — lands on Model Creation).
 */
export function decodeClientInAppSettingsInviteJwt(
  token: string,
  options?: { ignoreExpiration?: boolean },
): { email: string; name?: string | null; redirectTo: string } | null {
  const payload = decodeJwtPayload(token);
  if (!payload) return null;
  const typ = String(payload['typ'] ?? payload['type'] ?? '');
  if (typ !== CLIENT_IN_APP_SETTINGS_INVITE_TYP) return null;

  const exp = readExpSeconds(payload);
  if (!options?.ignoreExpiration && exp != null && Date.now() / 1000 > exp) {
    return null;
  }

  const email = String(payload['email'] ?? '').trim();
  if (!email) return null;

  const name = payload['name'] != null ? String(payload['name']) : null;
  const redirectTo =
    normalizeClientPostAuthRedirect(
      String(payload['redirect_to'] ?? payload['returnUrl'] ?? '').trim() || null,
    ) ?? CLIENT_IN_APP_SETTINGS_DEFAULT_REDIRECT;

  return { email, name, redirectTo };
}
